import { computed, DestroyRef, effect, inject, Injectable, type Signal, signal, untracked } from '@angular/core';

import { firstValueFrom } from 'rxjs';

import {
  abortStreaming,
  appendToken,
  buildActionCard,
  buildConversationTitle,
  buildTicketInfo,
  extractSources,
  finalizeStreaming,
  markTruncated,
} from '../../../shared/utils/leapbot-chat.util';
import { validateLeapBotFiles } from '../../../shared/utils/leapbot-files.util';
import {
  LEAPBOT_ERROR_COPY,
  LEAPBOT_NOTICE_COPY,
  LEAPBOT_SILENCE_COPY,
  LEAPBOT_SILENCE_TIMEOUT_MS,
  LEAPBOT_STREAM_IDLE_MS,
  LEAPBOT_STREAM_UNAVAILABLE_COPY,
} from '../../config/leapbot.config';
import type {
  LeapBotActionCard,
  LeapBotAgentEvent,
  LeapBotConnectionStatus,
  LeapBotConversation,
  LeapBotDraftAttachment,
  LeapBotErrorKind,
  LeapBotMessage,
  LeapBotSource,
} from '../../interfaces/leapbot.interface';
import { AuthService } from '../auth/auth.service';
import { LeapBotApiService, LeapBotRequestError } from './leapbot-api.service';
import { LeapBotContextService } from './leapbot-context.service';
import { LeapBotDiagnosticsService, type LeapBotRecordingResult } from './leapbot-diagnostics.service';
import { LeapBotHistoryService } from './leapbot-history.service';
import { type LeapBotChannelError, LeapBotSocketService } from './leapbot-socket.service';
import { LeapBotSoundService } from './leapbot-sound.service';

/** Message sent on the user's behalf after they authorized Jira from the action card. */
export const JIRA_AUTHORIZED_MESSAGE = 'Ya autoricé el acceso a Jira, por favor continúa creando el ticket.';

/** Message sent when the user asks LeapBot to continue an answer that was cut off. */
export const CONTINUE_MESSAGE = 'Continúa con tu respuesta anterior.';

const DRAFT_STORAGE_KEY = 'fury.leapbot.draft';
const AUTH_CHANNEL_CODES = new Set(['WS_UNAUTHORIZED', 'UNAUTHORIZED_THREAD_ACCESS', 'LEAPBOT_WS_ERROR']);

/**
 * Facade of the LeapBot chat: the active conversation, sending, the streamed answer, tool
 * activity, action cards, stopping, errors and history. It replaces the former mock chat.
 *
 * Sending is hybrid, as in LSS: the first message of a conversation (or any message while the
 * answer channel is closed) goes over HTTP and then opens the channel; follow-ups go straight
 * over the open channel.
 */
@Injectable({ providedIn: 'root' })
export class LeapBotChatService {
  private readonly auth = inject(AuthService);
  private readonly api = inject(LeapBotApiService);
  private readonly socket = inject(LeapBotSocketService);
  private readonly history = inject(LeapBotHistoryService);
  private readonly diagnostics = inject(LeapBotDiagnosticsService);
  private readonly context = inject(LeapBotContextService);
  private readonly sound = inject(LeapBotSoundService);

  private readonly conversationIdState = signal<string>(crypto.randomUUID());
  private readonly messagesState = signal<LeapBotMessage[]>([]);
  private readonly draftState = signal<string>(restoreDraft());
  private readonly attachmentsState = signal<LeapBotDraftAttachment[]>([]);
  private readonly attachmentNoticeState = signal<string | null>(null);
  private readonly openRequestsState = signal(0);
  private readonly awaitingState = signal(false);
  private readonly activeToolState = signal<string | null>(null);
  private readonly actionCardState = signal<LeapBotActionCard | null>(null);
  private pendingSources: LeapBotSource[] = [];
  /** True once the answer sound played for the current answer; reset when the user sends a message. */
  private answerSoundPlayed = false;
  private toolInput: Record<string, unknown> | undefined;
  private silenceTimer: ReturnType<typeof setTimeout> | null = null;
  private streamIdleTimer: ReturnType<typeof setTimeout> | null = null;

  /** Id of the open conversation; also LeapBot's `threadId`. */
  readonly conversationId: Signal<string> = this.conversationIdState.asReadonly();
  /** Messages of the open conversation. */
  readonly messages: Signal<LeapBotMessage[]> = this.messagesState.asReadonly();
  /** Text typed in the input and not yet sent; kept when a send fails. */
  readonly draft: Signal<string> = this.draftState.asReadonly();
  /** Files attached to the message being written. */
  readonly attachments: Signal<LeapBotDraftAttachment[]> = this.attachmentsState.asReadonly();
  /** Why some files were refused when attaching, or `null`. */
  readonly attachmentNotice: Signal<string | null> = this.attachmentNoticeState.asReadonly();
  /** Counts requests to open the chat from elsewhere in the app (see `openWithContext`); the shell reacts to it. */
  readonly openRequests: Signal<number> = this.openRequestsState.asReadonly();
  /** Whether a screen recording is in progress. */
  readonly isRecording: Signal<boolean> = this.diagnostics.isRecording;
  /** Seconds recorded so far. */
  readonly recordingSeconds: Signal<number> = this.diagnostics.elapsedSeconds;
  /** Name of the tool LeapBot is running, or `null`. */
  readonly activeTool: Signal<string | null> = this.activeToolState.asReadonly();
  /** Action LeapBot asked the user to take, or `null`. */
  readonly actionCard: Signal<LeapBotActionCard | null> = this.actionCardState.asReadonly();
  /** Connection state of the answer channel. */
  readonly connectionStatus: Signal<LeapBotConnectionStatus> = this.socket.status;
  /** Conversations saved in this browser for the current user, most recent first. */
  readonly conversations: Signal<LeapBotConversation[]> = this.history.conversations;

  /** True while an answer is streaming in. */
  readonly isStreaming: Signal<boolean> = computed(() => {
    const last = this.messagesState()[this.messagesState().length - 1];
    return last !== undefined && last.role === 'assistant' && !last.isComplete;
  });

  /** True from sending until the answer is complete: no new message can be sent meanwhile. */
  readonly isBusy: Signal<boolean> = computed(
    () => this.awaitingState() || this.activeToolState() !== null || this.isStreaming(),
  );

  /** True while the typing indicator is due: sent, but no answer text has arrived yet. */
  readonly isWaitingForAnswer: Signal<boolean> = computed(() => this.awaitingState() && !this.isStreaming());

  /** Why recording is unavailable right now, or `null` when the user can start it. */
  readonly recordDisabledReason: Signal<string | null> = computed(() => {
    if (!this.diagnostics.isSupported) return LEAPBOT_NOTICE_COPY.recordUnsupported;
    // A recording adds four files: video, console log, network log and context.
    if (this.attachmentsState().filter((a) => a.origin !== 'recording').length > 1) {
      return LEAPBOT_NOTICE_COPY.recordNoRoom;
    }
    return null;
  });

  /** Whether the record button can be used. */
  readonly canRecord: Signal<boolean> = computed(
    () => this.isEnabled() && !this.isBusy() && !this.isRecording() && this.recordDisabledReason() === null,
  );

  /** True when the last message is a failed send and its text is waiting in the input. */
  readonly canRetry: Signal<boolean> = computed(() => {
    const last = this.messagesState()[this.messagesState().length - 1];
    return !this.isBusy() && last !== undefined && last.isError === true && this.draftState().length > 0;
  });

  /** True when the last answer was cut off and the user may ask LeapBot to continue it. */
  readonly canContinue: Signal<boolean> = computed(() => {
    const last = this.messagesState()[this.messagesState().length - 1];
    return !this.isBusy() && last !== undefined && last.isTruncated === true;
  });

  /** Short text shown in the header: what the assistant is doing or whether it is reachable. */
  readonly statusLabel: Signal<string> = computed(() => {
    if (this.socket.status() === 'connecting') return LEAPBOT_NOTICE_COPY.statusConnecting;
    if (this.isBusy()) return LEAPBOT_NOTICE_COPY.statusTyping;
    if (this.socket.status() === 'error') return LEAPBOT_NOTICE_COPY.statusOffline;
    return LEAPBOT_NOTICE_COPY.statusOnline;
  });

  /** Whether the signed-in user may see the chat: internal roles only, never `client`. */
  readonly isVisible: Signal<boolean> = computed(
    () =>
      this.auth.isAuthenticated() &&
      this.auth.userId() !== null &&
      (this.auth.isSuperAdmin() || this.auth.mappedRoles().some((role) => role !== 'client')),
  );

  /** Whether the chat can be used: visible to the user and the build has a LeapBot URL. */
  readonly isEnabled: Signal<boolean> = computed(() => this.isVisible() && this.api.isConfigured);

  constructor() {
    this.socket.agentEvents$.subscribe((event) => this.onAgentEvent(event));
    this.socket.channelErrors$.subscribe((error) => this.onChannelError(error));
    this.socket.dropped$.subscribe(() => void this.onChannelDropped());
    this.diagnostics.finished$.subscribe((result) => this.attachRecording(result));
    // Signing out (or losing access) must stop a recording and close the answer channel.
    effect(() => {
      if (!this.isVisible()) untracked(() => this.shutdown());
    });
    inject(DestroyRef).onDestroy(() => this.clearTimers());
  }

  /**
   * Attaches files to the message being written after checking LeapBot's limits; files that
   * cannot be attached are explained in `attachmentNotice`.
   */
  addFiles(files: readonly File[], origin?: LeapBotDraftAttachment['origin']): void {
    if (!this.isEnabled() || files.length === 0) return;
    const { accepted, errors } = validateLeapBotFiles(this.attachmentsState().map((a) => a.file), files);
    if (accepted.length > 0) {
      this.attachmentsState.update((list) => [
        ...list,
        ...accepted.map((file) => ({
          id: crypto.randomUUID(),
          file,
          name: file.name,
          size: file.size,
          type: file.type,
          ...(origin ? { origin } : {}),
        })),
      ]);
    }
    this.attachmentNoticeState.set(errors.length > 0 ? errors.join(' ') : null);
  }

  /** Asks the browser to share a screen and starts recording; refusing the prompt is silent. */
  async startRecording(): Promise<void> {
    if (!this.canRecord()) return;
    const outcome = await this.diagnostics.start();
    if (outcome === 'failed') this.attachmentNoticeState.set(LEAPBOT_NOTICE_COPY.recordingFailed);
  }

  /** Stops the recording and attaches the video, the logs and the context file to the message. */
  async stopRecording(): Promise<void> {
    this.attachRecording(await this.diagnostics.stop());
  }

  /** Stops a recording in progress and discards it (chat closed or user signed out). */
  cancelRecording(): void {
    this.diagnostics.cancel();
  }

  /**
   * Opens the chat from another screen with a draft: the message is added after what the user
   * already typed, the files and, when asked, the context file are attached. Nothing is sent.
   * Does nothing for users who cannot use the chat.
   */
  openWithContext(options: { message?: string; files?: readonly File[]; includeContext?: boolean }): void {
    if (!this.isEnabled()) return;
    const { message, files, includeContext } = options;
    if (message) this.draftState.update((draft) => (draft.trim().length > 0 ? `${draft}\n${message}` : message));
    if (files && files.length > 0) this.addFiles(files);
    if (includeContext) {
      this.attachmentsState.update((list) => list.filter((a) => a.origin !== 'context'));
      this.addFiles([this.context.buildFile()], 'context');
    }
    this.openRequestsState.update((count) => count + 1);
  }

  /** Removes one file from the message being written. */
  removeAttachment(id: string): void {
    this.attachmentsState.update((list) => list.filter((a) => a.id !== id));
    this.attachmentNoticeState.set(null);
  }

  /** Replaces the text in the input. */
  setDraft(text: string): void {
    this.draftState.set(text);
  }

  /**
   * Sends the text as a new user message. Whitespace-only text, a pending answer or a user who
   * may not use the chat are ignored. On failure the text is put back in the input.
   */
  async send(text: string): Promise<void> {
    const trimmed = text.trim();
    const userId = this.auth.userId();
    const attached = this.attachmentsState();
    if (!this.isEnabled() || this.isBusy() || (trimmed.length === 0 && attached.length === 0)) return;
    if (userId === null) {
      this.failSend(trimmed, 'session_expired', attached);
      return;
    }

    const threadId = this.conversationIdState();
    this.draftState.set('');
    this.actionCardState.set(null);
    this.pendingSources = [];
    this.attachmentsState.set([]);
    this.attachmentNoticeState.set(null);
    this.updateMessages((msgs) => [
      ...msgs,
      {
        id: crypto.randomUUID(),
        role: 'user',
        content: trimmed,
        timestamp: now(),
        isComplete: true,
        ...(attached.length > 0
          ? { attachments: attached.map((a) => ({ name: a.name, size: a.size, type: a.type })) }
          : {}),
      },
    ]);
    this.awaitingState.set(true);
    this.armSilenceTimer();
    // Inside the user's click or key press, which also lets the browser play the answer sound later.
    this.answerSoundPlayed = false;
    this.sound.play('sent');

    try {
      // Text-only follow-ups go over the open channel; anything with files must go over HTTP.
      if (attached.length === 0 && this.socket.isConnectedTo(threadId)) {
        this.socket.sendMessage(trimmed);
        return;
      }
      const result = await firstValueFrom(
        this.api.sendMessage({ threadId, userId, message: trimmed, files: attached.map((a) => a.file) }),
      );
      if (result.filesRejected > 0 && threadId === this.conversationIdState()) {
        this.pushAssistantError(LEAPBOT_NOTICE_COPY.filesRejected(result.filesRejected));
      }
      await this.openChannel(threadId);
    } catch (error) {
      if (threadId !== this.conversationIdState()) return;
      this.handleSendFailure(trimmed, error, attached);
    }
  }

  /** Sends again the text kept in the input after a failed send, dropping the error it left. */
  retry(): Promise<void> {
    if (!this.canRetry()) return Promise.resolve();
    const text = this.draftState();
    this.updateMessages((msgs) => msgs.slice(0, -1));
    return this.send(text);
  }

  /** Asks LeapBot to continue an answer that was cut off. */
  continueAnswer(): Promise<void> {
    return this.canContinue() ? this.send(CONTINUE_MESSAGE) : Promise.resolve();
  }

  /** Sends the canned confirmation after the user authorized Jira from the action card. */
  confirmJiraAuthorization(): Promise<void> {
    return this.send(JIRA_AUTHORIZED_MESSAGE);
  }

  /**
   * Stops the answer in progress, keeping the text received so far marked `[Generación abortada]`
   * (or removing the answer when nothing arrived), and enables the input again.
   */
  stop(): void {
    this.socket.abort();
    this.clearTimers();
    this.awaitingState.set(false);
    this.activeToolState.set(null);
    this.updateMessages((msgs) => abortStreaming(msgs));
  }

  /** Starts an empty conversation; a pending answer of the previous one is stopped and kept. */
  newConversation(): void {
    this.leaveConversation();
    this.conversationIdState.set(crypto.randomUUID());
    this.messagesState.set([]);
    this.draftState.set('');
    this.attachmentsState.set([]);
    this.attachmentNoticeState.set(null);
  }

  /** Opens a saved conversation; a pending answer of the previous one is stopped and kept. */
  openConversation(id: string): void {
    const conversation = this.history.get(id);
    if (!conversation || id === this.conversationIdState()) return;
    this.leaveConversation();
    this.conversationIdState.set(conversation.id);
    this.messagesState.set(conversation.messages);
    this.draftState.set('');
    this.attachmentsState.set([]);
    this.attachmentNoticeState.set(null);
  }

  /** Deletes a conversation; if it was the open one an empty conversation opens. */
  deleteConversation(id: string): void {
    const wasOpen = id === this.conversationIdState();
    this.history.remove(id);
    if (wasOpen) this.newConversation();
  }

  /** Releases the answer channel and timers, for example on sign-out. */
  shutdown(): void {
    this.cancelRecording();
    this.clearTimers();
    this.socket.disconnect();
    this.awaitingState.set(false);
    this.activeToolState.set(null);
  }

  private leaveConversation(): void {
    this.cancelRecording();
    if (this.isBusy()) this.stop();
    this.socket.disconnect();
    this.actionCardState.set(null);
    this.clearTimers();
  }

  /** Replaces any previous recording with the new one, or explains why there is none. */
  private attachRecording(result: LeapBotRecordingResult | null): void {
    if (!result) {
      this.attachmentNoticeState.set(LEAPBOT_NOTICE_COPY.recordingFailed);
      return;
    }
    this.attachmentsState.update((list) => list.filter((a) => a.origin !== 'recording'));
    this.addFiles(
      [result.video, result.consoleLogs, result.networkLogs, this.context.buildFile()],
      'recording',
    );
    if (result.reachedLimit && this.attachmentNoticeState() === null) {
      this.attachmentNoticeState.set(LEAPBOT_NOTICE_COPY.recordingLimit);
    }
  }

  private async openChannel(threadId: string): Promise<void> {
    try {
      await this.socket.connect(threadId);
    } catch {
      if (threadId !== this.conversationIdState()) return;
      this.endPending();
      this.pushAssistantError(LEAPBOT_STREAM_UNAVAILABLE_COPY);
    }
  }

  private handleSendFailure(text: string, error: unknown, attached: LeapBotDraftAttachment[]): void {
    const kind: LeapBotErrorKind = error instanceof LeapBotRequestError ? error.kind : 'unreachable';
    this.failSend(text, kind, attached);
  }

  private failSend(text: string, kind: LeapBotErrorKind, attached: LeapBotDraftAttachment[]): void {
    this.endPending();
    this.attachmentsState.set(attached);
    // Drop the user message that never reached LeapBot and put its text back, so a retry does not duplicate it.
    this.updateMessages((msgs) => {
      const last = msgs[msgs.length - 1];
      return last && last.role === 'user' && last.content === text ? msgs.slice(0, -1) : msgs;
    });
    this.draftState.set(text);
    this.pushAssistantError(LEAPBOT_ERROR_COPY[kind]);
    if (kind === 'session_expired') this.startSignIn(text);
  }

  private startSignIn(text: string): void {
    try {
      sessionStorage.setItem(DRAFT_STORAGE_KEY, text);
    } catch {
      // Storage blocked: the draft is simply not restored after the redirect.
    }
    this.auth.login();
  }

  private onAgentEvent(event: LeapBotAgentEvent): void {
    if (event.threadId && event.threadId !== this.conversationIdState()) return;
    this.armSilenceTimerIfPending();
    switch (event.type) {
      case 'TokenStream':
        // The first chunk of an answer is the moment LeapBot starts to answer: one sound per answer, even if it uses a tool midway.
        if (!this.answerSoundPlayed) {
          this.answerSoundPlayed = true;
          this.sound.play('received');
        }
        this.awaitingState.set(false);
        this.activeToolState.set(null);
        this.updateMessages((msgs) => this.withPendingSources(appendToken(msgs, event.content, crypto.randomUUID(), now()), msgs.length));
        this.armStreamIdleTimer();
        break;
      case 'ToolStart':
        this.finalizeStream();
        this.toolInput = event.input;
        this.activeToolState.set(event.toolName);
        break;
      case 'ToolEnd': {
        this.activeToolState.set(null);
        const found = extractSources(event.toolName, event.output);
        if (found.length > 0) this.pendingSources = found;
        const ticket = buildTicketInfo(event.toolName, event.output, this.toolInput);
        if (ticket) {
          this.updateMessages((msgs) => [
            ...finalizeStreaming(msgs),
            {
              id: crypto.randomUUID(),
              role: 'assistant',
              content: `Ticket de Jira creado: ${ticket.key}`,
              timestamp: now(),
              isComplete: true,
              ticket,
            },
          ]);
        }
        break;
      }
      case 'ActionRequired':
        this.endPending();
        this.actionCardState.set(buildActionCard(event.data));
        break;
      case 'Error':
        this.endPending();
        this.pushAssistantError(event.message || LEAPBOT_ERROR_COPY.rejected);
        break;
      case 'partial_stream_error':
        this.endPending();
        this.updateMessages((msgs) => markTruncated(msgs));
        break;
    }
  }

  private onChannelError(error: LeapBotChannelError): void {
    // Aborting when nothing is generating is not an error for the user.
    if (error.code === 'NO_ACTIVE_GENERATION') return;
    this.endPending();
    // LeapBot treats every system_error as critical: the channel must be closed.
    this.socket.disconnect();
    const isAuth = AUTH_CHANNEL_CODES.has(error.code) || /authentic|unauthor/i.test(error.message);
    this.pushAssistantError(isAuth ? LEAPBOT_ERROR_COPY.session_expired : error.message || LEAPBOT_ERROR_COPY.rejected);
  }

  private async onChannelDropped(): Promise<void> {
    // Reopening never re-sends the message LeapBot already accepted.
    if (!this.isBusy()) return;
    await this.openChannel(this.conversationIdState());
  }

  /** Attaches the sources of the last knowledge search to an answer the first time it starts. */
  private withPendingSources(messages: LeapBotMessage[], previousLength: number): LeapBotMessage[] {
    if (this.pendingSources.length === 0 || messages.length <= previousLength) return messages;
    const sources = this.pendingSources;
    this.pendingSources = [];
    return messages.map((m, i) => (i === messages.length - 1 ? { ...m, sources } : m));
  }

  private endPending(): void {
    this.clearTimers();
    this.awaitingState.set(false);
    this.activeToolState.set(null);
    this.finalizeStream();
  }

  private finalizeStream(): void {
    if (this.streamIdleTimer) clearTimeout(this.streamIdleTimer);
    this.streamIdleTimer = null;
    this.updateMessages((msgs) => finalizeStreaming(msgs));
  }

  private pushAssistantError(content: string): void {
    this.updateMessages((msgs) => [
      ...msgs,
      { id: crypto.randomUUID(), role: 'assistant', content, timestamp: now(), isComplete: true, isError: true },
    ]);
  }

  private armSilenceTimer(): void {
    if (this.silenceTimer) clearTimeout(this.silenceTimer);
    this.silenceTimer = setTimeout(() => {
      this.endPending();
      this.pushAssistantError(LEAPBOT_SILENCE_COPY);
    }, LEAPBOT_SILENCE_TIMEOUT_MS);
  }

  private armSilenceTimerIfPending(): void {
    if (this.silenceTimer) this.armSilenceTimer();
  }

  private armStreamIdleTimer(): void {
    if (this.streamIdleTimer) clearTimeout(this.streamIdleTimer);
    this.streamIdleTimer = setTimeout(() => {
      this.streamIdleTimer = null;
      this.endPending();
    }, LEAPBOT_STREAM_IDLE_MS);
  }

  private clearTimers(): void {
    if (this.silenceTimer) clearTimeout(this.silenceTimer);
    if (this.streamIdleTimer) clearTimeout(this.streamIdleTimer);
    this.silenceTimer = null;
    this.streamIdleTimer = null;
  }

  private updateMessages(updater: (messages: LeapBotMessage[]) => LeapBotMessage[]): void {
    this.messagesState.update(updater);
    this.persist();
  }

  private persist(): void {
    const messages = this.messagesState();
    if (!messages.some((m) => m.role === 'user')) return;
    const id = this.conversationIdState();
    const stamp = now();
    this.history.upsert({
      id,
      title: buildConversationTitle(messages),
      messages,
      createdAt: this.history.get(id)?.createdAt ?? stamp,
      updatedAt: stamp,
    });
  }
}

function now(): string {
  return new Date().toISOString();
}

function restoreDraft(): string {
  try {
    const stored = sessionStorage.getItem(DRAFT_STORAGE_KEY);
    if (stored === null) return '';
    sessionStorage.removeItem(DRAFT_STORAGE_KEY);
    return stored;
  } catch {
    return '';
  }
}
